import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ADDON_LOCK_CONTENT, type AddonId } from '../../lib/config/addonLockContent';
import { useAddonGroupPrice } from '../../lib/hooks/useAddonGroupPrice';
import { WarehousePreview } from './previews/WarehousePreview';
import { NewsletterPreview } from './previews/NewsletterPreview';

interface AddonFeatureLockModalProps {
  addonId: AddonId;
  open: boolean;
  onClose: () => void;
  onActivate: () => void;
  onDemo: () => void;
}

const PREVIEW_MAP: Record<AddonId, React.ReactNode> = {
  warehouse: <WarehousePreview />,
  newsletter: <NewsletterPreview />,
};

/**
 * Modale mostrata quando si clicca in sidebar una sezione coperta da un add-on
 * che l'organizzazione non ha (vedi `_lockedAddon` in injectLockedFeatures.ts).
 *
 * Anatomia da dialog Notion, non da pop-up promozionale: raggio 8px, ombra
 * appena accennata, nessun riquadro icona colorato, titolo 16px, e le azioni
 * a fine lettura. Questa finestra ha come unico scopo la conversione, quindi
 * l'azione è a tutta larghezza col prezzo sotto: la sobrietà sta nella
 * cornice — raggio piccolo, ombra leggera, nessun gradiente — non
 * nell'indebolire il bottone.
 */
export const AddonFeatureLockModal: React.FC<AddonFeatureLockModalProps> = ({
  addonId,
  open,
  onClose,
  onActivate,
  onDemo,
}) => {
  const content = ADDON_LOCK_CONTENT[addonId];
  const { label: priceLabel } = useAddonGroupPrice(open && content ? content.addonGroup : null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open || !content) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#37352F]/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={content.title}
        className="relative w-full max-w-[460px] max-h-[88vh] overflow-y-auto rounded-lg bg-white dark:bg-[#202022] border border-[#EBEBEA] dark:border-white/10 shadow-[0_12px_32px_rgba(15,23,42,0.12),0_2px_8px_rgba(15,23,42,0.06)]"
      >
        <button
          onClick={onClose}
          aria-label="Chiudi"
          className="absolute top-3 right-3 p-1 rounded text-[#9B9A97] hover:text-[#37352F] dark:hover:text-white hover:bg-[#F7F6F3] dark:hover:bg-white/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-5 pt-5 pb-4">
          <h2 className="text-[16px] font-semibold text-[#37352F] dark:text-white leading-tight mb-1 pr-8">
            {content.title}
          </h2>
          <p className="text-[13.5px] text-[#787774] dark:text-white/50 leading-relaxed mb-4">
            {content.description}
          </p>

          {PREVIEW_MAP[addonId]}

          <ul className="mt-4 space-y-1.5">
            {content.features.map((f) => (
              <li
                key={f}
                className="flex items-start gap-2 text-[13px] text-[#37352F] dark:text-white/85 leading-snug"
              >
                <span className="shrink-0 mt-[7px] w-1 h-1 rounded-full bg-[#9B9A97]" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={onActivate}
            className="mt-4 w-full h-9 rounded text-[13.5px] font-medium text-white bg-[#E91E8C] hover:bg-[#C9187A] transition-colors"
          >
            {content.ctaPrimary}
          </button>
          {/* Nessun prezzo finché non arriva da subscription_addons: una cifra
              sbagliata sotto questo bottone è peggio di nessuna cifra. */}
          {priceLabel && (
            <p className="mt-2 text-center text-[12px] text-[#787774] dark:text-white/50">
              {priceLabel} · disattivi quando vuoi
            </p>
          )}
        </div>

        <div className="px-5 py-2.5 border-t border-[#EBEBEA] dark:border-white/10 bg-[#FBFBFA] dark:bg-white/[0.02] text-center">
          <button
            onClick={onDemo}
            className="text-[12px] text-[#787774] dark:text-white/50 hover:text-[#37352F] dark:hover:text-white transition-colors"
          >
            Preferisci vederlo dal vivo? Chiedi una demo
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
